import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useProjectsForPlaylists } from 'LearnerProjects';
import { useContentProgress } from 'ContentProgress';
import { useCurrentUser } from 'FrontRoyalAngular';
import { useDispatch, useSelector } from 'react-redux';
import { getPendingViewProjectFeedbackRequirementIdentifier, studentDashboardActions } from 'StudentDashboardSlice';
import { type ProjectGroupMembershipSansProjectGroup } from 'ProjectGroup';
import { ProjectFeedbackModal } from './ProjectFeedbackModal';

// This is only relevant when a project is focused based on projectRequirementIdentifier in the URL, and viewProjectFeedback=true.
// In that case we open this ProjectFeedbackModal component for the specified project.
const DirectLinkProjectFeedbackModalComponent = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const currentUser = useCurrentUser();
    const dispatch = useDispatch();
    const { projectsByPlaylist } = useProjectsForPlaylists();
    const { projectProgressByRequirementIdentifier } = useContentProgress();
    const pendingViewProjectFeedbackRequirementIdentifier = useSelector(
        getPendingViewProjectFeedbackRequirementIdentifier,
    );
    const [capturedProjectRequirementIdentifier, setCapturedProjectRequirementIdentifier] = useState<string | null>(
        null,
    );

    const closeModal = useCallback(() => {
        setCapturedProjectRequirementIdentifier(null);
        setIsModalOpen(false);
    }, [setIsModalOpen, setCapturedProjectRequirementIdentifier]);

    useEffect(() => {
        if (pendingViewProjectFeedbackRequirementIdentifier) {
            setCapturedProjectRequirementIdentifier(pendingViewProjectFeedbackRequirementIdentifier);
            dispatch(
                studentDashboardActions.setPendingViewProjectFeedbackRequirementIdentifier({
                    requirementIdentifier: null,
                }),
            );
        }
    }, [pendingViewProjectFeedbackRequirementIdentifier, setCapturedProjectRequirementIdentifier, dispatch]);

    const focusedProject = useMemo(() => {
        if (!capturedProjectRequirementIdentifier) return null;

        return (
            Object.values(projectsByPlaylist)
                ?.flat()
                ?.find(p => p.requirementIdentifier === capturedProjectRequirementIdentifier) ?? null
        );
    }, [capturedProjectRequirementIdentifier, projectsByPlaylist]);

    const progress = useMemo(
        () =>
            capturedProjectRequirementIdentifier
                ? projectProgressByRequirementIdentifier[capturedProjectRequirementIdentifier]
                : undefined,
        [capturedProjectRequirementIdentifier, projectProgressByRequirementIdentifier],
    );

    // Check if we have both score and feedback (same logic as ViewProjectFeedback component)
    const hasScoreAndFeedback = typeof progress?.score === 'number' && !!progress?.feedback;

    // Only render the modal if we have all required data, the project is passed or failed (not waived),
    // and we have both a score and feedback
    const shouldRenderModal = !!focusedProject && !!progress && !progress.waived && hasScoreAndFeedback;

    const passed = progress?.passed === true;
    const failed = progress?.passed === false;

    const acceptedProjectGroupMembership = useMemo<ProjectGroupMembershipSansProjectGroup | undefined>(
        () =>
            currentUser?.acceptedProjectGroupMemberships.find(
                (membership: ProjectGroupMembershipSansProjectGroup) =>
                    membership.requirementIdentifier === capturedProjectRequirementIdentifier,
            ),
        [currentUser?.acceptedProjectGroupMemberships, capturedProjectRequirementIdentifier],
    );

    useEffect(() => {
        if (shouldRenderModal) {
            setIsModalOpen(true);
        }
    }, [shouldRenderModal]);

    if (!shouldRenderModal) {
        return null;
    }

    return (
        <ProjectFeedbackModal
            closeModal={closeModal}
            isOpen={isModalOpen}
            progress={progress}
            project={focusedProject}
            passed={passed}
            failed={failed}
            acceptedProjectGroupMembership={acceptedProjectGroupMembership}
        />
    );
};

export const DirectLinkProjectFeedbackModal = memo(
    DirectLinkProjectFeedbackModalComponent,
) as unknown as typeof DirectLinkProjectFeedbackModalComponent;
